import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { eur } from "@/context/CartContext";
import { ShoppingBag, Loader2, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
    paid: "bg-emerald-50 text-emerald-600",
    pending: "bg-amber-50 text-amber-600",
    failed: "bg-red-50 text-red-600",
    expired: "bg-gray-100 text-gray-500",
};
const STATUS_LABEL = { paid: "Pago", pending: "Pendente", failed: "Falhou", expired: "Expirado" };

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/orders");
            setOrders(data);
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6" data-testid="admin-orders">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-xl lg:text-2xl font-extrabold text-primary inline-flex items-center gap-2"><ShoppingBag className="h-5 w-5" /> Encomendas</h1>
                    <p className="text-muted-foreground mt-1">Encomendas da loja e estado do pagamento.</p>
                </div>
                <button onClick={load} className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-muted"><RefreshCw className="h-4 w-4" /> Recarregar</button>
            </header>

            {loading ? (
                <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
            ) : orders.length === 0 ? (
                <div className="rounded-2xl border border-border bg-white p-10 text-center text-muted-foreground">Ainda não há encomendas.</div>
            ) : (
                <div className="rounded-2xl border border-border bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3">Data</th>
                                <th className="px-4 py-3">Cliente</th>
                                <th className="px-4 py-3">Artigos</th>
                                <th className="px-4 py-3">Total</th>
                                <th className="px-4 py-3">Pagamento</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {orders.map((o) => (
                                <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-muted/30 align-top">
                                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{String(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-primary">{o.customer?.name || "—"}</div>
                                        <div className="text-xs text-muted-foreground">{o.customer?.email || ""}</div>
                                        {o.customer?.phone ? <div className="text-xs text-muted-foreground">{o.customer.phone}</div> : null}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        {(o.items || []).map((it, i) => (
                                            <div key={i}>{it.quantity}× {it.name}</div>
                                        ))}
                                    </td>
                                    <td className="px-4 py-3 font-semibold text-primary whitespace-nowrap">{eur(o.amount_cents)}</td>
                                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.pending}`}>{STATUS_LABEL[o.payment_status] || o.payment_status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
